import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import posthog from 'posthog-js';
import { ExternalLink, RefreshCw, Mail, Lightbulb, Monitor } from 'lucide-react';
import { Logo } from '@/components/ui/Logo';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { useSession } from '@/lib/auth-client';
import { useI18n } from '@/lib/i18n';
import './UninstallFeedback.scss';

// PostHog configuration - uses environment variables
// Fork projects can disable analytics by not setting VITE_POSTHOG_KEY
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY || '';
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';
const TARGET_SURVEY_ID = '01973f4f-0d8e-0000-3335-187808bb1ade';

// Helper to check if analytics is enabled
const isAnalyticsEnabled = (): boolean => Boolean(POSTHOG_KEY);

// Survey types from PostHog
interface SurveyQuestion {
  id: string;
  question: string;
  description?: string;
  buttonText?: string;
  choices?: string[];
}

interface Survey {
  id: string;
  name: string;
  questions: SurveyQuestion[];
}

type Step = 'loading' | 'survey' | 'thankyou' | 'error';

export function UninstallFeedback() {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<Step>('loading');
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [selectedChoices, setSelectedChoices] = useState<string[]>([]);
  const [otherText, setOtherText] = useState<Record<number, string>>({});
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const posthogInitialized = useRef(false);
  const identityProcessed = useRef(false);

  const { t, locale } = useI18n();
  const isEn = locale === 'en';

  // Get distinct_id from URL params
  const distinctIdFromURL = searchParams.get('distinct_id');

  // Get session for logged-in user
  const { data: session, isPending: isSessionPending } = useSession();
  const user = session?.user;

  // Initialize PostHog (without identifying yet)
  useEffect(() => {
    if (posthogInitialized.current) return;
    posthogInitialized.current = true;

    // Skip initialization if no key configured (fork projects without PostHog)
    if (!isAnalyticsEnabled()) {
      console.info('[Sokuji] [Uninstall Feedback] PostHog analytics disabled (VITE_POSTHOG_KEY not set)');
      return;
    }

    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
    });

    // Expose to window for debugging
    if (typeof window !== 'undefined') {
      (window as typeof window & { posthog: typeof posthog }).posthog = posthog;
    }
  }, []);

  // Handle user identification and aliasing after session is loaded
  useEffect(() => {
    // Wait for session to finish loading and PostHog to be initialized
    if (isSessionPending || !posthogInitialized.current || identityProcessed.current) return;
    identityProcessed.current = true;

    const userId = user?.id;

    if (userId && distinctIdFromURL) {
      // Both logged in and has URL distinct_id: identify with user.id and alias
      posthog.identify(userId, {
        email: user?.email,
        name: user?.name,
      });
      // Alias the extension's distinct_id to the logged-in user
      // This links the extension tracking data to the user's account
      posthog.alias(distinctIdFromURL, userId);
      console.debug('[Sokuji] [Uninstall Feedback] Identified user and aliased extension distinct_id');
    } else if (userId) {
      // Only logged in: identify with user.id
      posthog.identify(userId, {
        email: user?.email,
        name: user?.name,
      });
      console.debug('[Sokuji] [Uninstall Feedback] Identified logged-in user');
    } else if (distinctIdFromURL) {
      // Only URL distinct_id: identify with that
      posthog.identify(distinctIdFromURL);
      console.debug('[Sokuji] [Uninstall Feedback] Identified with URL distinct_id');
    } else {
      // Neither: anonymous user (PostHog generates anonymous ID)
      console.debug('[Sokuji] [Uninstall Feedback] Anonymous user');
    }
  }, [isSessionPending, user, distinctIdFromURL]);

  // Fetch survey from PostHog
  const fetchSurvey = useCallback(() => {
    setStep('loading');
    setErrorMessage('');

    posthog.getSurveys((surveys) => {
      const targetSurvey = surveys.find((s) => s.id === TARGET_SURVEY_ID) as Survey | undefined;

      if (!targetSurvey) {
        setErrorMessage('Survey not found. Please contact support.');
        setStep('error');
        return;
      }

      if (!targetSurvey.questions || targetSurvey.questions.length === 0) {
        setErrorMessage('Invalid survey configuration.');
        setStep('error');
        return;
      }

      setSurvey(targetSurvey);
      setStep('survey');

      posthog.capture('survey shown', {
        $survey_id: targetSurvey.id,
      });
    }, true);
  }, []);

  // Fetch survey on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSurvey();
    }, 500);

    return () => clearTimeout(timer);
  }, [fetchSurvey]);

  // Handle survey dismissed
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (survey && step === 'survey') {
        posthog.capture('survey dismissed', {
          $survey_id: survey.id,
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [survey, step]);

  // Handle choice selection
  const handleChoiceChange = (choice: string, checked: boolean, index: number) => {
    if (checked) {
      setSelectedChoices((prev) => [...prev, choice]);
    } else {
      setSelectedChoices((prev) => prev.filter((c) => c !== choice));
      if (choice.toLowerCase().includes('other')) {
        setOtherText((prev) => {
          const newState = { ...prev };
          delete newState[index];
          return newState;
        });
      }
    }
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!survey || !survey.questions || survey.questions.length === 0) {
      return;
    }

    setIsSubmitting(true);

    const responses = selectedChoices.map((choice) => {
      if (choice.toLowerCase().includes('other')) {
        const index = survey.questions[0].choices?.findIndex(
          (c) => c.toLowerCase() === choice.toLowerCase()
        );
        if (index !== undefined && index >= 0 && otherText[index]?.trim()) {
          return `Other: ${otherText[index].trim()}`;
        }
      }
      return choice;
    });

    const responseKey = `$survey_response_${survey.questions[0].id}`;
    posthog.capture('survey sent', {
      $survey_id: survey.id,
      [responseKey]: responses,
    });

    setTimeout(() => {
      setIsSubmitting(false);
      setStep('thankyou');
    }, 300);
  };

  const question = survey?.questions[0];

  return (
    <div className="uninstall-feedback">
      <div className="uninstall-feedback__container">
        {/* Header */}
        <div className="uninstall-feedback__header">
          <Link to="/" className="uninstall-feedback__logo">
            <Logo size={40} />
            <span>Sokuji</span>
          </Link>
        </div>

        {/* Card */}
        <div className="uninstall-feedback__card">
          {/* Loading State */}
          {step === 'loading' && (
            <div className="uninstall-feedback__loading">
              <div className="uninstall-feedback__spinner" />
              <p>{t('uninstall.loading')}</p>
            </div>
          )}

          {/* Error State */}
          {step === 'error' && (
            <div className="uninstall-feedback__error">
              <Alert variant="error">{errorMessage}</Alert>
              <Button variant="secondary" onClick={fetchSurvey}>
                <RefreshCw size={16} />
                {t('uninstall.retry')}
              </Button>
            </div>
          )}

          {/* Survey State */}
          {step === 'survey' && (
            <>
              <div className="uninstall-feedback__card-header">
                <h1 className="uninstall-feedback__title">
                  {isEn ? (question?.question || t('uninstall.fallbackTitle')) : t('uninstall.fallbackTitle')}
                </h1>
                <p className="uninstall-feedback__subtitle">
                  {isEn ? (question?.description || t('uninstall.fallbackSubtitle')) : t('uninstall.fallbackSubtitle')}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="uninstall-feedback__form">
                <div className="uninstall-feedback__choices uninstall-feedback__choices--grid">
                  {question?.choices?.map((choice, index) => {
                    const isOther = choice.toLowerCase().includes('other');
                    const choiceId = `choice-${index}`;
                    const isChecked = selectedChoices.includes(choice);

                    return (
                      <div key={index} className="uninstall-feedback__choice">
                        <label
                          htmlFor={choiceId}
                          className={`uninstall-feedback__choice-label ${isChecked ? 'uninstall-feedback__choice-label--selected' : ''}`}
                        >
                          <input
                            type="checkbox"
                            id={choiceId}
                            checked={isChecked}
                            onChange={(e) => handleChoiceChange(choice, e.target.checked, index)}
                            className="uninstall-feedback__checkbox"
                          />
                          <span className="uninstall-feedback__checkmark" />
                          <span className="uninstall-feedback__choice-text">{choice}</span>
                        </label>
                        {isOther && isChecked && (
                          <input
                            type="text"
                            placeholder={t('uninstall.otherPlaceholder')}
                            value={otherText[index] || ''}
                            onChange={(e) =>
                              setOtherText((prev) => ({ ...prev, [index]: e.target.value }))
                            }
                            className="uninstall-feedback__other-input"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="uninstall-feedback__actions">
                  <Button type="submit" fullWidth loading={isSubmitting}>
                    {isEn ? (question?.buttonText || t('uninstall.submitButton')) : t('uninstall.submitButton')}
                  </Button>
                </div>
              </form>

              {/* Bottom bar: contact & desktop app */}
              <div className="uninstall-feedback__bottom-bar">
                <div className="uninstall-feedback__bottom-links">
                  <div className="uninstall-feedback__help-row">
                    <Mail size={14} className="uninstall-feedback__help-icon" />
                    <span>
                      <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>
                      {' · '}
                      <a href="https://github.com/kizuna-ai-lab/sokuji/issues" target="_blank" rel="noopener noreferrer">GitHub Issues</a>
                    </span>
                  </div>
                  <div className="uninstall-feedback__help-row">
                    <Lightbulb size={14} className="uninstall-feedback__help-icon" />
                    <span>
                      <a href="https://github.com/kizuna-ai-lab/sokuji/discussions" target="_blank" rel="noopener noreferrer">GitHub Discussions</a>
                    </span>
                  </div>
                </div>
                <div className="uninstall-feedback__bottom-divider" />
                <div className="uninstall-feedback__bottom-desktop">
                  <Monitor size={14} className="uninstall-feedback__help-icon" />
                  <span>{t('uninstall.tryDesktop').split('{link}')[0]}<a href="https://sokuji.kizuna.ai/" target="_blank" rel="noopener noreferrer">Sokuji Desktop</a>{t('uninstall.tryDesktop').split('{link}')[1]}</span>
                </div>
              </div>
            </>
          )}

          {/* Thank You State */}
          {step === 'thankyou' && (
            <div className="uninstall-feedback__thankyou">
              <div className="uninstall-feedback__card-header">
                <h1 className="uninstall-feedback__title uninstall-feedback__title--success">
                  {t('uninstall.thankyouTitle')}
                </h1>
                <p className="uninstall-feedback__subtitle">
                  {t('uninstall.thankyouSubtitle')}
                </p>
              </div>

              <div className="uninstall-feedback__cta-group">
                <p className="uninstall-feedback__cta-text">
                  {t('uninstall.reinstallPrompt')}
                </p>
                <a
                  href="https://chromewebstore.google.com/detail/sokuji-extension/ppmihnhelgfpjomhjhpecobloelicnak"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="uninstall-feedback__link-button"
                >
                  <ExternalLink size={16} />
                  {t('uninstall.reinstallButton')}
                </a>
              </div>

              <div className="uninstall-feedback__divider" />

              <div className="uninstall-feedback__cta-group">
                <h2 className="uninstall-feedback__cta-title">{t('uninstall.needMoreFeatures')}</h2>
                <p className="uninstall-feedback__cta-text">
                  {t('uninstall.needMoreFeaturesDesc')}
                </p>
                <a href="https://sokuji.kizuna.ai/" className="uninstall-feedback__link-button uninstall-feedback__link-button--accent">
                  <ExternalLink size={16} />
                  {t('uninstall.tryDesktopClient')}
                </a>
              </div>

              <div className="uninstall-feedback__divider" />

              <div className="uninstall-feedback__cta-group">
                <h2 className="uninstall-feedback__cta-title">{t('uninstall.getInTouch')}</h2>
                <div className="uninstall-feedback__help-links">
                  <div className="uninstall-feedback__help-row">
                    <Mail size={14} className="uninstall-feedback__help-icon" />
                    <span>
                      {t('uninstall.reportBug')}{' '}
                      <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>
                      {' · '}
                      <a href="https://github.com/kizuna-ai-lab/sokuji/issues" target="_blank" rel="noopener noreferrer">GitHub Issues</a>
                    </span>
                  </div>
                  <div className="uninstall-feedback__help-row">
                    <Lightbulb size={14} className="uninstall-feedback__help-icon" />
                    <span>
                      {t('uninstall.shareIdea')}{' '}
                      <a href="https://github.com/kizuna-ai-lab/sokuji/discussions" target="_blank" rel="noopener noreferrer">GitHub Discussions</a>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="uninstall-feedback__footer">
          <p>{t('uninstall.copyright').replace('{year}', String(new Date().getFullYear()))}</p>
        </footer>
      </div>
    </div>
  );
}
